/**
 * История задачи — представление TaskEvent (§20.5).
 *
 * Читается только на чтение: события append-only, править их нельзя.
 * Текст описания приходит с сервера, чтобы история в интерфейсе и запись
 * в аудите совпадали дословно.
 */

import { TaskSection } from "@taskflow/contracts";

import type { WsError } from "@taskflow/client-core";
import { ErrorState, SkeletonRows } from "@/shared/ui";

import { useTaskEventsQuery } from "@taskflow/client-core/tasks";
import { Section } from "./Section";

export function TaskHistory({ taskKey }: { taskKey: string }) {
  const events = useTaskEventsQuery(taskKey);

  return (
    <Section title="История" section={TaskSection.HISTORY} count={events.data?.data.length}>
      {events.isLoading && <SkeletonRows rows={3} />}

      {events.error !== undefined && (
        <ErrorState
          message={(events.error as WsError).data.message}
          onRetry={() => void events.refetch()}
        />
      )}

      {events.data !== undefined &&
        (events.data.data.length === 0 ? (
          <p className="text-[13px] text-faint">История пуста</p>
        ) : (
          <ol className="flex flex-col gap-3">
            {events.data.data.map((event) => (
              <li key={event.id} className="flex gap-3">
                <span
                  className="mt-1.5 size-1.5 shrink-0 rounded-full bg-line-strong"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] text-fg">{event.summary}</p>
                  <p className="text-[11px] text-faint">
                    {event.actorName} · {event.occurredAtLabel}
                  </p>

                  {event.changes !== undefined && event.changes.length > 0 && (
                    <dl className="mt-1 flex flex-col gap-0.5 text-[11px]">
                      {event.changes.map((change) => (
                        <div key={change.field} className="flex gap-1.5">
                          <dt className="text-faint">{change.field}:</dt>
                          <dd className="min-w-0">
                            <span className="text-faint line-through">
                              {change.before ?? "пусто"}
                            </span>
                            <span className="mx-1 text-faint">→</span>
                            <span className="text-fg">{change.after ?? "пусто"}</span>
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              </li>
            ))}
          </ol>
        ))}
    </Section>
  );
}
